import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Play, Sliders } from "lucide-react";
import {
  ExitManagementCard, SizingCard,
  RUN_LABELS, RunSelect, NumInput, Pnl, SignedText, Stability, usd, pct0, isNum,
  MIN_N, smallSample, ci95, CiText, SmallSampleNote,
} from "@/components/EdgeTools";
import {
  Badge, Button, Checkbox, DecisionBadge, EmptyState, Icon, IconButton, PageHead, Panel, RuleResult, Skeleton,
  TextField, fmt, usePageTitle,
} from "@/sentinel";

// Entry checkpoints on the equity curve: categorical viz tokens, each with its own dash so hue is never the only cue.
const RUN_STYLE = {
  2: { color: "var(--viz-1)", dash: undefined },
  3: { color: "var(--viz-2)", dash: "6 4" },
  4: { color: "var(--viz-3)", dash: "2 3" },
  5: { color: "var(--viz-5)", dash: "10 3 2 3" },
};

/** Tracks an element's rendered width so SVG charts draw at 1:1 (no stretched text). */
function useWidth(fallback = 960) {
  const [el, setEl] = useState(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(320, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, w];
}

// Claude plain-English callout. Renders only when the backend supplies one.
function AISummary({ text, testId, onRefresh, refreshing }) {
  if (!text) return null;
  return (
    <div className="sn-stack" data-testid={testId}
         style={{ gap: "var(--space-1)", padding: "var(--space-3)", background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: "var(--radius-md)" }}>
      <div className="sn-row" style={{ justifyContent: "space-between" }}>
        <span className="sn-micro">AI summary · Claude</span>
        {onRefresh && (
          <IconButton icon="RefreshCw" size="sm" label="Regenerate this summary (bypasses the 24h cache)"
                      onClick={onRefresh} disabled={refreshing} aria-busy={refreshing || undefined}
                      data-testid={testId ? `${testId}-refresh` : undefined} />
        )}
      </div>
      <p style={{ margin: 0, color: "var(--text-primary)" }}>{text}</p>
    </div>
  );
}

function EquityChart({ intervals }) {
  const [wrapRef, W] = useWidth(1160);
  const series = (intervals || []).filter((s) => (s.equity || []).length);
  if (!series.length) return null;
  const H = 260, PL = 64, PR = 18, PT = 16, PB = 26;
  const maxLen = Math.max(...series.map((s) => s.equity.length));
  const allY = series.flatMap((s) => s.equity.map((p) => p.cum_pnl)).concat([0]);
  const yMin = Math.min(...allY), yMax = Math.max(...allY);
  const yPad = (yMax - yMin) * 0.08 || 100;
  const lo = yMin - yPad, hi = yMax + yPad;
  const x = (i) => PL + (maxLen <= 1 ? 0 : (i / (maxLen - 1)) * (W - PL - PR));
  const y = (v) => PT + (1 - (v - lo) / (hi - lo || 1)) * (H - PT - PB);
  const zeroY = y(0);
  const ticks = [hi, (hi + lo) / 2, lo].map((v) => Math.round(v));
  const lastOf = (s) => s.equity[s.equity.length - 1]?.cum_pnl ?? 0;
  const summary = series.map((s) => `${s.label} ${usd(lastOf(s), 0)}`).join(", ");
  return (
    <Panel title="Equity curve · cumulative P&L per entry checkpoint" icon="ChartLine" data-testid="bt-equity-chart">
      <div className="sn-stack">
        <div ref={wrapRef} style={{ width: "100%" }}>
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }} role="img"
               aria-label={`Equity curves by entry checkpoint. Final cumulative P&L: ${summary}.`}>
            {ticks.map((t, i) => (
              <g key={i}>
                <line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke="var(--viz-grid)" strokeWidth="1" />
                <text x={PL - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--viz-axis)" className="sn-num">{usd(t, 0)}</text>
              </g>
            ))}
            <line x1={PL} x2={W - PR} y1={zeroY} y2={zeroY} stroke="var(--text-tertiary)" strokeWidth="1" />
            {series.map((s) => {
              const st = RUN_STYLE[s.entry_run] || { color: "var(--viz-8)" };
              const d = s.equity.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.cum_pnl).toFixed(1)}`).join(" ");
              return <path key={s.entry_run} d={d} fill="none" stroke={st.color} strokeDasharray={st.dash} strokeWidth="1.5" />;
            })}
          </svg>
        </div>
        <div className="sn-row" style={{ gap: "var(--space-4)" }}>
          {series.map((s) => {
            const st = RUN_STYLE[s.entry_run] || { color: "var(--viz-8)" };
            return (
              <span key={s.entry_run} className="sn-row sn-small" style={{ gap: 6 }} data-testid={`bt-equity-legend-${s.entry_run}`}>
                <svg width="18" height="4" aria-hidden="true"><line x1="0" x2="18" y1="2" y2="2" stroke={st.color} strokeWidth="2" strokeDasharray={st.dash} /></svg>
                <span className="sn-muted">{s.label}</span>
                <Pnl value={lastOf(s)} dp={0} />
              </span>
            );
          })}
        </div>
        <p className="sn-small sn-faint" style={{ margin: 0 }}>
          X-axis is trade number (chronological). Each line is realized cumulative P&amp;L to settlement for that entry time.
        </p>
      </div>
    </Panel>
  );
}

function CalibrationCard() {
  const [run, setRun] = useState(3);
  const { data, isFetching, isError } = useQuery({
    queryKey: ["bt-calibration", run],
    queryFn: () => api.btCalibration(run),
    retry: false,
  });
  const bins = (data?.bins || []).filter((b) => b.n > 0);
  const W = 420, H = 300, PL = 44, PR = 16, PT = 16, PB = 38;
  const px = (p) => PL + p * (W - PL - PR);
  const py = (p) => PT + (1 - p) * (H - PT - PB);
  const maxN = Math.max(1, ...bins.map((b) => b.n));
  // Brier vs the baseline of always predicting the base rate. `informative` comes from the API; older servers
  // only send brier_score, so the baseline is derived from the realised hit rate: p(1 − p).
  const brier = data?.brier ?? data?.brier_score ?? null;
  const baseRate = data?.overall_realized;
  const brierBaseline = data?.brier_baseline ?? (isNum(baseRate) ? Number((baseRate * (1 - baseRate)).toFixed(4)) : null);
  const informative = data?.informative ?? (brier != null && brierBaseline != null ? brier < brierBaseline : null);
  const overallDiff = data?.overall_realized != null && data?.overall_predicted != null
    ? (data.overall_realized - data.overall_predicted) * 100 : null;

  return (
    <Panel title="Probability calibration · is 60% really 60%?" icon="Target" data-testid="bt-calibration"
           actions={<RunSelect value={run} onChange={setRun} testId="bt-calibration-run" label={null} />}>
      <div className="sn-stack">
        {isError && <p className="sn-muted" style={{ margin: 0 }} data-testid="bt-calibration-empty">No stored backtest for this entry checkpoint yet. Run Compare intervals or a single backtest first, then reopen.</p>}
        {isFetching && <Skeleton lines={3} />}

        {data && !isError && (
          <div className="sn-row" style={{ gap: "var(--space-6)", alignItems: "flex-start" }}>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: 420, height: "auto" }} role="img"
                 aria-label={`Reliability diagram: predicted vs realized P(profit), ${bins.length} deciles, Brier ${brier ?? "unavailable"}${brierBaseline != null ? ` vs baseline ${brierBaseline}` : ""}.`}>
              {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                <g key={t}>
                  <line x1={px(t)} x2={px(t)} y1={PT} y2={H - PB} stroke="var(--viz-grid)" strokeWidth="1" />
                  <line x1={PL} x2={W - PR} y1={py(t)} y2={py(t)} stroke="var(--viz-grid)" strokeWidth="1" />
                  <text x={px(t)} y={H - PB + 15} textAnchor="middle" fontSize="11" fill="var(--viz-axis)" className="sn-num">{t}</text>
                  <text x={PL - 6} y={py(t) + 4} textAnchor="end" fontSize="11" fill="var(--viz-axis)" className="sn-num">{t}</text>
                </g>
              ))}
              <line x1={px(0)} y1={py(0)} x2={px(1)} y2={py(1)} stroke="var(--text-secondary)" strokeWidth="1" strokeDasharray="4 3" />
              {bins.map((b, i) => (
                <circle key={i} cx={px(b.predicted)} cy={py(b.realized)} r={3 + 6 * (b.n / maxN)}
                  fill={b.realized >= b.predicted ? "var(--viz-div-pos-2)" : "var(--viz-div-neg-2)"} fillOpacity="0.85"
                  data-testid={`bt-calibration-point-${i}`}>
                  <title>{`predicted ${(b.predicted * 100).toFixed(0)}% · realized ${(b.realized * 100).toFixed(0)}% · n=${b.n}`}</title>
                </circle>
              ))}
              <text x={(PL + W - PR) / 2} y={H - 4} textAnchor="middle" fontSize="11" fill="var(--viz-axis)">Predicted P(profit)</text>
              <text x={12} y={(PT + H - PB) / 2} textAnchor="middle" fontSize="11" fill="var(--viz-axis)" transform={`rotate(-90 12 ${(PT + H - PB) / 2})`}>Realized</text>
            </svg>

            <div className="sn-stack" style={{ minWidth: 220, flex: 1 }}>
              <div className="sn-stat sn-stat-md">
                <span className="sn-stat-k">Brier score vs baseline (lower is better)</span>
                <span className="sn-row" style={{ gap: "var(--space-2)" }}>
                  <span className="sn-stat-v" data-testid="bt-brier">{brier ?? "—"}</span>
                  <span className="sn-small sn-muted">baseline <span className="sn-num" data-testid="bt-brier-baseline">{brierBaseline ?? "—"}</span></span>
                  {informative != null && (
                    <RuleResult result={informative ? "PASS" : "FAIL"} data-testid="bt-brier-informative">{informative ? "BEATS BASELINE" : "UNINFORMATIVE"}</RuleResult>
                  )}
                </span>
                {informative === false && (
                  <span className="sn-stat-sub" data-testid="bt-brier-uninformative-note">
                    No better than always predicting the {pct0(baseRate)} base rate: the model's probabilities carry no information yet.
                  </span>
                )}
              </div>
              <div className="sn-stat sn-stat-sm">
                <span className="sn-stat-k">Predicted vs realized (overall)</span>
                <span className="sn-stat-v sn-row" style={{ gap: "var(--space-2)" }}>
                  <span>{pct0(data.overall_predicted)} → {pct0(data.overall_realized)}</span>
                  {overallDiff != null && <SignedText value={overallDiff}>{overallDiff >= 0 ? "+" : "−"}{Math.abs(overallDiff).toFixed(0)}pp</SignedText>}
                </span>
              </div>
              {smallSample(data.n) && <SmallSampleNote n={data.n} what="calibration" />}
              <p className="sn-small sn-faint" style={{ margin: 0 }}>
                n={data.n} trades · <span className="sn-num">{data.start} → {data.end}</span><br />
                Points on the dashed diagonal are well calibrated. Above it (blue) the model is under-confident; below it (orange), over-confident. Bubble size is the sample per decile.
              </p>
            </div>
          </div>
        )}

        {data && !isError && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CalibGroupTable title="By structure · predicted vs realized" rows={data.by_structure} labels={{ A: "A · Bear put", B: "B · Bull call", C: "C · Bull put credit" }} testId="bt-calib-structure" />
            <CalibGroupTable title="By width · predicted vs realized" rows={data.by_width} labels={{}} testId="bt-calib-width" />
          </div>
        )}
      </div>
    </Panel>
  );
}

function CalibGroupTable({ title, rows, labels, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)", minWidth: 0 }} data-testid={testId}>
      <span className="sn-micro">{title}</span>
      <div className="sn-table-wrap">
        <table className="sn-table" aria-label={title}>
          <thead><tr><th>Group</th><th className="r">n</th><th className="r">Predicted</th><th className="r">Realized</th><th className="r">Δ</th><th className="r">Brier</th></tr></thead>
          <tbody>
            {rows.map((g) => {
              const diff = (g.realized - g.predicted) * 100;
              return (
                <tr key={g.group} data-testid={`${testId}-${g.group}`}>
                  <td style={{ color: "var(--text-primary)" }}>{labels[g.group] || g.group}</td>
                  <td className="num">{g.n}</td>
                  <td className="num">{pct0(g.predicted)}</td>
                  <td className="num">{pct0(g.realized)}</td>
                  <td className="num"><SignedText value={diff}>{diff >= 0 ? "+" : "−"}{Math.abs(diff).toFixed(0)}pp</SignedText></td>
                  <td className="num">{g.brier}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="sn-stat sn-stat-md" data-testid={testId}>
      <span className="sn-stat-k">{label}</span>
      <span className="sn-stat-v">{value}</span>
    </div>
  );
}

function ExpTable({ title, groups, testId }) {
  const keys = Object.keys(groups || {});
  if (!keys.length) return null;
  return (
    <Panel flush title={title} data-testid={testId}>
      <div style={{ overflowX: "auto" }}>
        <table className="sn-table" aria-label={title}>
          <thead><tr><th>Group</th><th className="r">Trades</th><th className="r">Hit rate</th><th className="r">Expectancy</th><th className="r">Avg win</th><th className="r">Avg loss</th><th className="r">Total P&amp;L</th><th className="r">PF</th></tr></thead>
          <tbody>
            {keys.map((k) => {
              const g = groups[k];
              return (
                <tr key={k}>
                  <td style={{ color: "var(--text-primary)" }}>{k}</td>
                  <td className="num">{g.n}</td>
                  <td className="num">{pct0(g.hit_rate)}</td>
                  <td className="num"><Pnl value={g.expectancy} /></td>
                  <td className="num">{usd(g.avg_win)}</td>
                  <td className="num">{usd(g.avg_loss)}</td>
                  <td className="num"><Pnl value={g.total_pnl} dp={0} /></td>
                  <td className="num">{g.profit_factor ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function FillDeltaCard() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => { api.btFillDelta().then(setData).catch(() => {}); }, []);
  const refresh = async () => {
    setRefreshing(true);
    try { setData(await api.btFillDelta(true)); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setRefreshing(false); }
  };
  if (!data) return null;
  return (
    <Panel title="Real fill vs modeled · slippage per interval" icon="Split" data-testid="bt-fill-delta"
           meta={<span className="sn-num">{data.start} → {data.end}</span>}>
      <div className="sn-stack">
        <p className="sn-small sn-muted" style={{ margin: 0 }}>
          How much expectancy the real bid/ask spread costs against the modeled haircut (the default is 0.10 index points, which is $10 per spread), per trade. A larger figure means that interval&apos;s real spread gives up more edge.
        </p>
        {data.narrative && (
          <AISummary text={data.narrative} testId="fill-delta-narrative"
                     onRefresh={refresh} refreshing={refreshing} />
        )}
        <div className="sn-table-wrap">
          <table className="sn-table" aria-label="Real fill vs modeled per entry checkpoint">
            <thead><tr><th>Entry checkpoint</th><th className="r">Trades</th><th className="r">Modeled exp.</th><th className="r">Real exp.</th><th className="r">Slippage / trade</th><th className="r">Real fills</th></tr></thead>
            <tbody>
              {data.by_interval.map((r) => (
                <tr key={r.entry_run}>
                  <td style={{ color: "var(--text-primary)" }}>{r.label}</td>
                  <td className="num">{r.n}</td>
                  <td className="num"><Pnl value={r.modeled_exp} /></td>
                  <td className="num"><Pnl value={r.real_exp} /></td>
                  <td className="num">{r.slippage != null ? "−" + usd(r.slippage) : "—"}</td>
                  <td className="num" title={r.median_fill_lag_s != null ? `median lag ${r.median_fill_lag_s}s` : ""}>{r.real_fill_pct != null ? pct0(r.real_fill_pct) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

export default function Backtest() {
  usePageTitle("Backtest");
  const { can } = useAuth();
  const isOwner = can("owner");
  const [start, setStart] = useState("2026-05-18");
  const [end, setEnd] = useState("2026-05-22");
  const [entryRun, setEntryRun] = useState(3);
  const [haircut, setHaircut] = useState(0.10);
  const [commission, setCommission] = useState(0.65);
  const [ingesting, setIngesting] = useState(false);
  const [qIngesting, setQIngesting] = useState(false);
  // Real NBBO fills by default: the comparison that loads on its own is the real-fill one (cache id suffix "-real").
  const [realFills, setRealFills] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [compare, setCompare] = useState(null);
  const [comparing, setComparing] = useState(false);

  const { data: coverage, refetch: refetchCov } = useQuery({ queryKey: ["bt-coverage"], queryFn: api.btCoverage });
  const { data: status } = useQuery({ queryKey: ["bt-ingest-status"], queryFn: api.btIngestStatus, refetchInterval: ingesting ? 3000 : false });
  const { data: qStatus } = useQuery({ queryKey: ["bt-qingest-status"], queryFn: api.btIngestQuotesStatus, refetchInterval: qIngesting ? 3000 : false });

  // Auto-load the most recent cached compare (e.g. the pre-computed full 2-year run)
  // so the user sees the real answer immediately, without waiting 4-5 minutes.
  useEffect(() => {
    if (compare) return;
    api.btCompareCached(null, null, realFills).then((c) => setCompare({ ...c, cached: true }))
      .catch(() => {/* no cache yet — user can click Compare */});
  }, [realFills]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status && ingesting && !status.running) {
      setIngesting(false);
      toast.success(`Ingestion complete — ${status.done}/${status.total} days (${status.errors} errors).`);
      refetchCov();
    }
  }, [status, ingesting]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (qStatus && qIngesting && !qStatus.running) {
      setQIngesting(false);
      toast.success(`Quote ingest complete — ${qStatus.quotes} NBBO quotes across ${qStatus.done} days.`);
      refetchCov();
    }
  }, [qStatus, qIngesting]); // eslint-disable-line react-hooks/exhaustive-deps

  const startIngest = async () => {
    try {
      await api.btIngest({ start, end });
      setIngesting(true);
      toast.info("Ingestion started (one-time). Data is catalogued in the DB for reuse.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    }
  };

  const startQuoteIngest = async () => {
    try {
      await api.btIngestQuotes({ start, end });
      setQIngesting(true);
      toast.info("Real NBBO quote ingest started (Advanced plan). Fetches bid/ask at each entry checkpoint.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    }
  };

  const runBacktest = async () => {
    setRunning(true);
    try {
      const r = await api.btRun({ start, end, entry_run: entryRun, haircut: Number(haircut), commission: Number(commission), real_fills: realFills });
      setResult(r);
      const o = r.stats.overall;
      if (smallSample(o.n)) toast.info(`Backtest done — ${o.n || 0} trades. Too few to judge (fewer than ${MIN_N}).`);
      else toast[o.expectancy >= 0 ? "success" : "error"](`Backtest done — ${o.n} trades, expectancy ${usd(o.expectancy)}/trade.`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const o = result?.stats?.overall;

  const runCompare = async () => {
    setComparing(true);
    try {
      const r = await api.btCompare({ start, end, entry_run: entryRun, haircut: Number(haircut), commission: Number(commission), real_fills: realFills });
      setCompare(r);
      toast.success(`Compared ${r.by_interval.length} entry checkpoints${realFills ? " (real NBBO fills)" : ""}.`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setComparing(false);
    }
  };

  const intervals = useMemo(() => compare?.by_interval || [], [compare]);
  const bestInterval = intervals[0];
  const bestSmall = !bestInterval || smallSample(bestInterval.n);
  // "Best" is only claimed for a positive mean; a negative top row is "least bad", never best.
  const bestPositive = isNum(bestInterval?.expectancy) && Number(bestInterval.expectancy) > 0;
  const showCi = intervals.some((r) => ci95(r));

  return (
    <>
      <PageHead
        crumb={["Research", "Backtest"]}
        title="Backtest"
        context="Profit expectancy for SPXW 0DTE. Replays the deterministic engine over stored history; results are research, not advice, and Sentinel places no orders."
      />
      <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-density="compact" data-testid="backtest-page">
        <div className="sn-grid-12">
          {/* Controls */}
          <Panel className="sn-col-8" title="Run a backtest" icon="FlaskConical">
            <div className="sn-stack">
              <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
                <div style={{ width: 160 }}>
                  <TextField label="Start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="bt-start" />
                </div>
                <div style={{ width: 160 }}>
                  <TextField label="End" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="bt-end" />
                </div>
                <Button icon="Download" loading={ingesting} onClick={startIngest} disabled={!isOwner} title={isOwner ? undefined : "Owner only"} data-testid="bt-ingest-btn">
                  {ingesting ? "Ingesting…" : "1) Ingest data"}
                </Button>
                <Button icon="Download" loading={qIngesting} onClick={startQuoteIngest} disabled={!isOwner} data-testid="bt-ingest-quotes-btn"
                        title={isOwner ? "Fetch real NBBO bid/ask at each entry checkpoint for the range above" : "Owner only"}>
                  {qIngesting ? "Ingesting quotes…" : "1b) Ingest NBBO quotes"}
                </Button>
              </div>
              {!isOwner && <span className="sn-help">Ingesting data is owner-only; you can run backtests on what is already stored.</span>}
              {(ingesting && status) || (qIngesting && qStatus) ? (
                <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-4)" }}>
                  {ingesting && status && (
                    <span className="sn-num" data-testid="bt-ingest-progress">
                      Data {status.done}/{status.total} days · {status.last_day || "…"}
                    </span>
                  )}
                  {qIngesting && qStatus && (
                    <span className="sn-num" data-testid="bt-qingest-progress">
                      Quotes {qStatus.done}/{qStatus.total} days · {qStatus.quotes} quotes · {qStatus.last_day || "…"}
                    </span>
                  )}
                </div>
              ) : null}
              <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
                <RunSelect value={entryRun} onChange={setEntryRun} testId="bt-entry-run" />
                <NumInput label="Slippage haircut (index points per spread, 0.10 = $10)" value={haircut} onChange={setHaircut} width={300} testId="bt-haircut" />
                <NumInput label="Commission ($/leg)" value={commission} onChange={setCommission} width={144} testId="bt-commission" />
              </div>
              <div className="sn-row" style={{ gap: "var(--space-3)" }}>
                <Button variant="primary" icon={Play} loading={running} onClick={runBacktest} data-testid="bt-run-btn">
                  {running ? "Running…" : "2) Run backtest"}
                </Button>
                <Button icon="ArrowDownUp" loading={comparing} onClick={runCompare} data-testid="bt-compare-btn">
                  {comparing ? "Comparing…" : "Compare intervals"}
                </Button>
                <span data-testid="bt-real-fills-toggle"
                      title="Options Advanced: fill at real historical NBBO (ask long / bid short) instead of the modeled haircut">
                  <Checkbox label="Real NBBO fills (Advanced)" checked={realFills}
                            onChange={(v) => { setRealFills(v); setCompare(null); setResult(null); }}
                            data-testid="bt-real-fills-checkbox" />
                </span>
              </div>
              <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-1)" }} data-testid="bt-coverage">
                <Icon name="Database" size={14} /> Catalogued days in store:{" "}
                <b className="sn-num" style={{ color: "var(--text-primary)" }}>{coverage?.ok ?? 0}</b> ok /{" "}
                <span className="sn-num">{coverage?.count ?? 0}</span> total
                <span className="sn-faint">·</span>
                <b className="sn-num" style={{ color: "var(--text-primary)" }}>{coverage?.quoted ?? 0}</b> with real NBBO quotes
              </div>
            </div>
          </Panel>

          <Panel className="sn-col-4" title="Method" icon="BookOpen" data-testid="bt-method-note">
            <p className="sn-small sn-muted" style={{ margin: 0 }}>
              Replays the deterministic engine over stored history (ingest once, query many), derives SPX via put-call parity and marks realized P&amp;L to settlement.
              With <b style={{ color: "var(--text-primary)" }}>Real NBBO fills</b> (Options Advanced) the chosen spread is filled executably — long leg at the historical ask, short leg at the bid at the entry checkpoint —
              instead of the modeled traded price ∓ haircut. Real NBBO fills is ticked by default; untick it to see the modeled-fill version. Ingest quotes first if none are stored. Confirmation and breadth
              signals are not reconstructed historically, so this measures the engine&apos;s fundamental (L / E / T) pick. Results under {MIN_N} trades are illustrative and never headlined.
            </p>
          </Panel>
        </div>

        {/* Interval comparison */}
        {compare && (
          <>
            {bestInterval && bestSmall && (
              <Panel title={<>Entry checkpoints · <span className="sn-num">{compare.start} → {compare.end}</span></>} icon="Target" data-testid="bt-best-interval-small">
                <SmallSampleNote n={bestInterval.n} what="which entry checkpoint is best" />
              </Panel>
            )}
            {bestInterval && !bestSmall && (
              <Panel
                title={<>{bestPositive ? "Best entry checkpoint" : "No positive configuration"} · <span className="sn-num">{compare.start} → {compare.end}</span></>}
                icon="Target"
                data-testid="bt-best-interval"
                meta={
                  <>
                    {compare.real_fills && (
                      <Badge icon="Check" data-testid="bt-real-fills-badge" title="Filled at real historical NBBO (ask long / bid short)">
                        REAL NBBO FILLS
                      </Badge>
                    )}
                    {compare.cached && (
                      <Badge icon="Database" data-testid="bt-cached-badge" title={`Pre-computed at ${compare.computed_at || "—"}`}>
                        CACHED
                      </Badge>
                    )}
                  </>
                }
              >
                {!bestPositive && (
                  <p className="sn-small" style={{ margin: "0 0 var(--space-3)", color: "var(--text-primary)" }} data-testid="bt-best-interval-none">
                    Every entry checkpoint lost money per trade over this range, so none is recommended. The least bad one is shown for reference.
                  </p>
                )}
                <div className="sn-row" style={{ gap: "var(--space-8)", alignItems: "flex-start" }}>
                  <div className="sn-stat">
                    <span className="sn-stat-k">{bestPositive ? "Entry checkpoint" : "Least bad checkpoint"}</span>
                    <span style={{ fontSize: 18, lineHeight: "24px", fontWeight: 600, color: "var(--text-primary)" }}>{bestInterval.label}</span>
                  </div>
                  <Metric label="Expectancy / trade" value={<Pnl value={bestInterval.expectancy} />} />
                  {ci95(bestInterval) && <Metric label="95% CI / trade" value={<CiText r={bestInterval} />} />}
                  <Metric label="Profit factor" value={bestInterval.profit_factor ?? "—"} />
                  <Metric label="Hit rate" value={pct0(bestInterval.hit_rate)} />
                  <Metric label="Trades" value={bestInterval.n} />
                  <Metric label="Total P&L" value={<Pnl value={bestInterval.total_pnl} dp={0} />} />
                </div>
              </Panel>
            )}

            <EquityChart intervals={intervals} />

            <Panel flush title={<>Which interval is most right · <span className="sn-num">{compare.start} → {compare.end}</span></>}
                   icon="ListOrdered" data-testid="bt-compare-result">
              <div style={{ overflowX: "auto" }}>
                <table className="sn-table" aria-label="Entry checkpoints ranked by expectancy">
                  <thead><tr><th>Entry checkpoint</th><th className="r">Trades</th><th className="r">Hit rate</th><th className="r">Expectancy</th>{showCi && <th className="r">95% CI</th>}<th className="r">Total P&amp;L</th><th className="r">Profit factor</th>{compare.real_fills && <th className="r">Real fills</th>}</tr></thead>
                  <tbody>
                    {intervals.map((r, i) => (
                      <tr key={r.entry_run}>
                        <td style={{ color: "var(--text-primary)" }}>
                          <span className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "nowrap" }}>{r.label}{i === 0 && !smallSample(r.n) && bestPositive && <Badge>Best</Badge>}{smallSample(r.n) && r.n > 0 && <span className="sn-small sn-faint">n &lt; {MIN_N}</span>}</span>
                        </td>
                        <td className="num">{r.n}</td>
                        <td className="num">{r.n ? pct0(r.hit_rate) : "—"}</td>
                        <td className="num">{r.n ? <Pnl value={r.expectancy} /> : "—"}</td>
                        {showCi && <td className="num"><CiText r={r} /></td>}
                        <td className="num">{r.n ? <Pnl value={r.total_pnl} dp={0} /> : "—"}</td>
                        <td className="num">{r.profit_factor ?? "—"}</td>
                        {compare.real_fills && <td className="num" title={r.median_fill_lag_s != null ? `median fill lag ${r.median_fill_lag_s}s` : ""}>{r.n ? pct0(r.real_fill_pct ?? 0) : "—"}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}

        {/* Results */}
        {o && (
          <>
            <Panel title={<>Result · {RUN_LABELS[result.entry_run]} · <span className="sn-num">{result.start} → {result.end}</span></>} icon="TrendingUp">
              <div className="sn-stack">
                {smallSample(o.n) && <SmallSampleNote n={o.n} what="this backtest" data-testid="bt-small-sample" />}
                {smallSample(o.n) ? (
                  <div className="sn-table-wrap" data-testid="bt-overall">
                    <table className="sn-table" aria-label="Backtest summary (small sample)">
                      <thead><tr><th className="r">Trades</th><th className="r">Hit rate</th><th className="r">Expectancy</th>{ci95(o) && <th className="r">95% CI</th>}<th className="r">Total P&amp;L</th><th className="r">Avg win</th><th className="r">Avg loss</th><th className="r">PF</th></tr></thead>
                      <tbody><tr>
                        <td className="num" data-testid="bt-n">{o.n ?? 0}</td>
                        <td className="num" data-testid="bt-hitrate">{pct0(o.hit_rate)}</td>
                        <td className="num" data-testid="bt-expectancy"><Pnl value={o.expectancy} /></td>
                        {ci95(o) && <td className="num"><CiText r={o} /></td>}
                        <td className="num" data-testid="bt-total"><Pnl value={o.total_pnl} dp={0} /></td>
                        <td className="num">{usd(o.avg_win)}</td>
                        <td className="num">{usd(o.avg_loss)}</td>
                        <td className="num" data-testid="bt-pf">{o.profit_factor ?? "—"}</td>
                      </tr></tbody>
                    </table>
                  </div>
                ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-4" data-testid="bt-overall">
                  <Metric label="Trades" value={o.n} testId="bt-n" />
                  <Metric label="Hit rate" value={pct0(o.hit_rate)} testId="bt-hitrate" />
                  <Metric label="Expectancy / trade" value={<Pnl value={o.expectancy} />} testId="bt-expectancy" />
                  <Metric label="Total P&L" value={<Pnl value={o.total_pnl} dp={0} />} testId="bt-total" />
                  <Metric label="Avg win" value={usd(o.avg_win)} />
                  <Metric label="Avg loss" value={usd(o.avg_loss)} />
                  <Metric label="Profit factor" value={o.profit_factor ?? "—"} testId="bt-pf" />
                  {ci95(o) && <Metric label="95% CI / trade" value={<CiText r={o} />} />}
                </div>
                )}
                <span className="sn-small sn-faint">
                  Days evaluated: <span className="sn-num">{o.days}</span> · Stood aside (NO TRADE): <span className="sn-num">{o.stood_aside}</span>
                </span>
              </div>
            </Panel>

            <div className="sn-grid-12">
              <div className="sn-col-6"><ExpTable title="By option structure" groups={result.stats.by_structure} testId="bt-by-structure" /></div>
              <div className="sn-col-6"><ExpTable title="By width" groups={result.stats.by_width} testId="bt-by-width" /></div>
            </div>

            {result.stats.when_wrong?.length > 0 && (
              <Panel flush title="When it was wrong (worst losers)" icon="TriangleAlert" data-testid="bt-when-wrong">
                <div style={{ overflowX: "auto" }}>
                  <table className="sn-table" aria-label="Worst losing trades">
                    <thead><tr><th>Day</th><th>Structure</th><th className="r">Width</th><th className="r">Short K</th><th className="r">Settle</th><th className="r">P&amp;L</th><th>Why</th><th>Avoidable?</th></tr></thead>
                    <tbody>
                      {result.stats.when_wrong.map((w, i) => (
                        <tr key={i}>
                          <td className="sn-num" style={{ fontSize: 12 }}>{w.day}</td>
                          <td style={{ color: "var(--text-primary)" }}>{w.structure}</td>
                          <td className="num">{w.width}</td>
                          <td className="num">{num(w.short_strike, "strike")}</td>
                          <td className="num">{num(w.settle, "index")}</td>
                          <td className="num"><Pnl value={w.pnl} /></td>
                          <td style={{ whiteSpace: "normal", minWidth: 200 }}>{w.reason}</td>
                          <td style={{ whiteSpace: "normal", minWidth: 160, color: "var(--text-tertiary)" }}>{w.avoidable_note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            <Panel flush title="Trades" icon="ListOrdered" data-testid="bt-trades" meta={<span className="sn-num">{result.trades.length}</span>}>
              <div className="sn-table-wrap" style={{ border: 0, borderRadius: 0, maxHeight: 560 }}>
                <table className="sn-table" aria-label="Backtest trades">
                  <thead><tr><th>Day</th><th>Decision</th><th>Structure</th><th className="r">W</th><th className="r">Short / long</th><th className="r">Spot</th><th className="r">Settle</th><th className="r">Entry</th><th className="r">P&amp;L</th></tr></thead>
                  <tbody>
                    {result.trades.map((t, i) => (
                      <tr key={i}>
                        <td className="sn-num" style={{ fontSize: 12 }}>{t.day}</td>
                        <td>{t.decision_label ? <DecisionBadge decision={t.decision_label} size="compact" muted /> : "—"}</td>
                        {t.stood_aside ? (
                          <td colSpan={7} className="sn-faint">Stood aside — no position</td>
                        ) : (
                          <>
                            <td style={{ color: "var(--text-primary)" }}>{t.structure}</td>
                            <td className="num">{t.width}</td>
                            <td className="num">{num(t.short_strike, "strike")} / {num(t.long_strike, "strike")}</td>
                            <td className="num">{num(t.spot, "index")}</td>
                            <td className="num">{num(t.settle, "index")}</td>
                            <td className="num">{num(t.entry_net, "price")}</td>
                            <td className="num"><Pnl value={t.pnl} /></td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}

        <CalibrationCard />

        <FillDeltaCard />

        <ExitManagementCard start={start} end={end} haircut={haircut} commission={commission} realFills={realFills} />
        <SizingCard />
        {/* Roadmap Y5 (owner, 2026-10-14): the edge finder, threshold tuner and regime explorer were removed. They
            searched the old rule set for its best setting, which the 10:00 am programme's no-tuning rule forbids. */}
      </div>
    </>
  );
}
